import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, Search, ArrowRight, ArrowUpRight } from 'lucide-react';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { blogPosts } from '../mock';

export default function Blog() {
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('All');
  const cats = ['All', ...Array.from(new Set(blogPosts.map(b => b.category)))];
  const filtered = useMemo(() => blogPosts.filter(p => {
    const okC = cat === 'All' || p.category === cat;
    const okQ = !q || p.title.toLowerCase().includes(q.toLowerCase());
    return okC && okQ;
  }), [q, cat]);
  const featured = blogPosts[0];

  return (
    <>
      <section className="relative pt-40 pb-20 bg-[#0B1B33] overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]"
          style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)', backgroundSize: '48px 48px' }} />
        <div className="absolute -bottom-32 right-0 w-[500px] h-[500px] bg-amber-500/10 rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-xs uppercase tracking-[0.2em] text-amber-400 mb-4">Latest news & insights</div>
          <h1 className="text-5xl md:text-6xl font-bold text-white tracking-tight leading-[1.05] max-w-3xl">
            From our blog.
          </h1>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl">
            Insights, breakdowns and field notes from the Core Estimation team — written for the people who price the work.
          </p>
        </div>
      </section>

      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          {featured && (
            <Link to={`/blog/${featured.slug}`} className="group block mb-14">
              <Card className="grid md:grid-cols-2 overflow-hidden rounded-3xl border-slate-100 bg-white hover:shadow-2xl transition-all duration-300">
                <div className="relative h-72 md:h-auto overflow-hidden">
                  <img src={featured.image} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <Badge className="absolute top-5 left-5 bg-amber-500 text-[#0B1B33] hover:bg-amber-500">Featured</Badge>
                </div>
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 self-start">{featured.category}</Badge>
                  <h2 className="mt-4 text-3xl md:text-4xl font-bold text-[#0B1B33] tracking-tight leading-tight group-hover:text-amber-600 transition">{featured.title}</h2>
                  <p className="mt-4 text-slate-600 leading-relaxed">{featured.excerpt}</p>
                  <div className="mt-6 flex items-center gap-4 text-xs text-slate-500">
                    <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {featured.date}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {featured.readTime}</span>
                  </div>
                  <div className="mt-6 inline-flex items-center text-[#0B1B33] font-semibold group-hover:text-amber-600 transition">
                    Read article <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition" />
                  </div>
                </div>
              </Card>
            </Link>
          )}

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-10">
            <div className="flex flex-wrap gap-2">
              {cats.map(c => (
                <button key={c} onClick={() => setCat(c)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                    cat === c ? 'bg-[#0B1B33] text-amber-400' : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
                  }`}>
                  {c}
                </button>
              ))}
            </div>
            <div className="relative w-full md:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles…" className="pl-10 h-11 rounded-xl bg-white" />
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((p) => (
              <Link to={`/blog/${p.slug}`} key={p.slug} className="group">
                <Card className="bg-white rounded-2xl overflow-hidden border-slate-100 h-full hover:shadow-2xl hover:-translate-y-1 transition-all duration-300">
                  <div className="relative h-52 overflow-hidden">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                    <Badge className="absolute top-4 left-4 bg-amber-500 text-[#0B1B33] hover:bg-amber-500 font-semibold">{p.category}</Badge>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 text-xs text-slate-500">
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {p.date}</span>
                      <span>·</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {p.readTime}</span>
                    </div>
                    <h3 className="mt-3 text-lg font-bold text-[#0B1B33] group-hover:text-amber-600 transition leading-snug">{p.title}</h3>
                    <p className="mt-3 text-sm text-slate-600 line-clamp-2">{p.excerpt}</p>
                    <div className="mt-5 inline-flex items-center text-sm font-semibold text-[#0B1B33] group-hover:text-amber-600 transition">
                      Read article <ArrowUpRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 transition" />
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0B1B33] tracking-tight">Get our monthly cost letter.</h2>
          <p className="mt-3 text-slate-600">Real labor + material movements, by region and trade — delivered every first Monday.</p>
          <form className="mt-7 flex flex-col sm:flex-row gap-3 max-w-md mx-auto" onSubmit={(e) => e.preventDefault()}>
            <Input type="email" placeholder="you@company.com" className="h-12 rounded-xl" />
            <Button className="bg-amber-500 hover:bg-amber-600 text-[#0B1B33] font-semibold h-12 px-6 rounded-xl">Subscribe</Button>
          </form>
        </div>
      </section>
    </>
  );
}
